import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {L10nTranslationService} from 'angular-l10n';
import {Decimal} from 'decimal.js';
import {combineLatestWith, defer, EMPTY, merge, Observable} from 'rxjs';
import {fromPromise} from 'rxjs/internal/observable/innerFrom';
import {catchError, finalize, switchMap, takeUntil} from 'rxjs/operators';
import {erc20Abi, parseEther} from 'viem';
import {TypedForm, TypedFormControls} from '../../common/form-types';
import {getFormControlValueWithUpdatesStream} from '../../common/form-utils';
import {AutocompleteConfig} from '../../components/autocomplete';
import {
    DeliverableWalletFilter
} from '../../components/deliverable-input/deliverable-autocomplete/filters/deliverable-wallet-filter';
import {destroy$} from '../../components/destroyable';
import {CommonBlockchainUtilService} from '../../components/dex/common-blockchain-util.service';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {Deliverable, isDeliverablesEqual} from '../../components/dex/dex-streaming/types/deliverable';
import {numToV, vToDec} from '../../components/dex/dex-streaming/utils';
import {DeliverableBalance} from '../../components/dex/dyn-balances/deliverable-balance';
import {Erc20BalancesSharedService} from '../../components/dex/dyn-balances/erc20-balances-shared.service';
import {Erc20BalancesService} from '../../components/dex/dyn-balances/erc20-balances.service';
import {DynWallet} from '../../components/dynamic-xyz/dyn-xyz-wc/dyn-remote';
import {DynamicZyxDaemonService} from '../../components/dynamic-xyz/dynamic-zyx-daemon.service';
import {Widget} from '../../components/layout/widget/widget';
import {AppConfigService} from '../../services/config.service';
import {bindControlsToSettings, bindControlToSettings} from '../components/widget-impl-settings-component-base';
import {DexKnownError} from '../dex-lp-trade-widget/trade-execution/dex-known-errors';
import {DexTxError} from '../dex-lp-trade-widget/trade-execution/dex-tx-error';
import {TxHistoryRecFactory} from '../dex-tx-history-widget/tx-history-rec.factory';
import {TxApiStorageMiddlewareService} from '../dex-tx-history-widget/tx-storage/tx-api-storage-middleware.service';
import {DexTransferWidget} from './dex-transfer-widget';

interface FormValue {
    from: string;
    to: string;
    asset: Deliverable;
    qty: number;
}

const WALLETS_MUST_BE_DIFFERENT_ERROR = 'walletsMustBeDifferent';

function walletsMustBeDifferentValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const form = control as TypedForm<FormValue>;
        const controls = form.controls;

        const fromWallet = controls.from.value;
        const toWallet = controls.to.value;

        if (fromWallet && toWallet && fromWallet === toWallet) {
            const errors: ValidationErrors = {};
            errors[WALLETS_MUST_BE_DIFFERENT_ERROR] = true;
            return errors;
        }

        return null;
    };
}

interface ErrorInfo {
    dexTxError?: DexTxError;
    message?: string;
}

@Component({
    selector:        'dex-transfer-widget',
    templateUrl:     './dex-transfer-widget.component.html',
    styleUrls:       ['./dex-transfer-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders:   [
        DeliverableWalletFilter
    ],
    standalone:      false
})
export class DexTransferWidgetComponent implements OnInit, OnDestroy {

    public readonly destroy$ = destroy$();

    public form: TypedForm<FormValue>;
    public readonly $isTransferring = signal<boolean>(false);
    public readonly $transferError = signal<ErrorInfo | null>(null);
    public readonly $transferSuccess = signal<boolean>(false);
    public readonly $transactionHash = signal<string | null>(null);
    public readonly $transactionLink = signal<string | null>(null);

    public balance$: Observable<DeliverableBalance>;

    public deliverableAutocompleteConfig: AutocompleteConfig = {
        type: {
            provider: undefined,
            filters: [this.deliverableWalletFilter]
        }
    };

    public walletToAutocompleteConfig: AutocompleteConfig = {
        freeTyping: true
    };

    public readonly WALLETS_MUST_BE_DIFFERENT_ERROR = WALLETS_MUST_BE_DIFFERENT_ERROR;

    public readonly $isFormSubmitted = signal<boolean>(false);

    private readonly nativeCurrency = this.appConfigService.appConfig.dex.chain.nativeCurrency;

    constructor(
        @Inject(Widget)
        public widget: DexTransferWidget,
        private dynamicZyxDaemonService: DynamicZyxDaemonService,
        public translationService: L10nTranslationService,
        private dexSecuritiesSubjectService: DexSecuritiesSubjectService,
        private deliverableWalletFilter: DeliverableWalletFilter,
        private appConfigService: AppConfigService,
        private erc20BalancesService: Erc20BalancesService,
        private commonBlockchainUtilService: CommonBlockchainUtilService,
        private erc20BalancesSharedService: Erc20BalancesSharedService,
        private txHistoryRecFactory: TxHistoryRecFactory,
        private txApiStorageMiddlewareService: TxApiStorageMiddlewareService
    ) {}

    public ngOnInit(): void {
        this.buildForm();
    }

    public ngOnDestroy(): void {
        // Support for Language decorator.
    }

    public onTransfer(): void {
        this.$isFormSubmitted.set(true);

        if (this.form.invalid || this.$isTransferring()) {
            return;
        }

        this.$isTransferring.set(true);
        this.$transferError.set(null);
        this.$transferSuccess.set(false);
        this.form.disable();

        fromPromise(this.processTransfer()).pipe(
            catchError(e => {
                const isDexTxError = e instanceof DexTxError;
                this.$transferError.set({
                    dexTxError: isDexTxError ? e : undefined,
                    message: isDexTxError ? undefined : 'Unknown error occurred'
                });
                this.$isTransferring.set(false);

                if (!isDexTxError) {
                    console.error(e);
                }

                return EMPTY;
            }),
            finalize(() => {
                this.form.enable();
                this.$isTransferring.set(false);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    public onSetQuickAmount(event: MouseEvent, delBalance: DeliverableBalance): void {
        event.preventDefault();
        event.stopPropagation();

        if (this.form.enabled && this.form.controls.qty.enabled) {
            const qty = vToDec(delBalance.balance, delBalance.deliverable.decimalPlaces).toNumber();
            this.form.controls.qty.setValue(qty);
        }
    }

    private buildForm(): void {
        const controls: TypedFormControls<FormValue> = {
            from:  new FormControl<string>(null, Validators.required),
            to:    new FormControl<string>(null, Validators.required),
            asset: new FormControl<Deliverable>(null, Validators.required),
            qty:   new FormControl<number>(null, [Validators.required, Validators.min(0)])
        };

        merge(
            bindControlsToSettings({
                widget: this.widget,
                dict:   {
                    fromAddress: controls.from,
                    toAddress:   controls.to
                }
            }),
            bindControlToSettings({
                widget:            this.widget,
                control:           controls.asset,
                settingsFieldName: 'assetSymbol',
                controlValueEqualityPredicate:
                                   isDeliverablesEqual,
                convertValueFromSettingsToControl:
                                   symbol => symbol
                                       ? this.dexSecuritiesSubjectService.getDeliverableBySymbol(symbol)
                                       : null,
                convertValueFromControlToSettings:
                                   d => d && d.symbol
            }),
            bindControlToSettings({
                widget:            this.widget,
                control:           controls.qty,
                settingsFieldName: 'qty',
                convertValueFromSettingsToControl:
                                   qtyStr => qtyStr ? parseFloat(qtyStr) : null,
                convertValueFromControlToSettings:
                                   qty => qty === null || qty === undefined ? undefined : String(qty)
            })
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();

        this.form = new FormGroup(controls, { validators: walletsMustBeDifferentValidator() });
        this.balance$ = this.createBalanceStream();
    }

    private async processTransfer(): Promise<`0x${string}`> {
        const wFrom = this.getWalletFrom();

        if (!wFrom) {
            throw new Error('Source wallet not found');
        }

        const formValue: FormValue = this.form.value;
        const d = formValue.asset;
        const isNativeCurrency = d.symbol === this.nativeCurrency.symbol;
        const walletClient = await wFrom.getWalletClient();
        const publicClient = this.commonBlockchainUtilService.getPublicClient();
        const tokenAddress = isNativeCurrency ? undefined : d.identifier;
        let txHash: `0x${string}` | undefined;

        try {
            if (isNativeCurrency) {
                const qty = parseEther(new Decimal(formValue.qty).toFixed());

                await publicClient.estimateGas({
                    account: walletClient.account,
                    to: formValue.to as `0x${string}`,
                    value: qty
                });

                txHash = await walletClient.sendTransaction({
                    to:      formValue.to as `0x${string}`,
                    value:   qty,
                    kzg:     undefined,
                    account: walletClient.account,
                    chain:   walletClient.chain
                });
            } else {
                const dQty = numToV(formValue.qty, d.decimalPlaces);
                const tQty = await this.erc20BalancesService.convertDelUnitsToToken(d, dQty);

                await publicClient.estimateContractGas({
                    address:      tokenAddress as `0x${string}`,
                    abi:          erc20Abi,
                    functionName: 'transfer',
                    args:         [
                        formValue.to as `0x${string}`,
                        tQty
                    ],
                    account:      walletClient.account
                });

                txHash = await walletClient.writeContract({
                    address:      tokenAddress as `0x${string}`,
                    abi:          erc20Abi,
                    functionName: 'transfer',
                    args:         [
                        formValue.to as `0x${string}`,
                        tQty
                    ],
                    account:      walletClient.account,
                    chain:        walletClient.chain
                });
            }

            this.$transferSuccess.set(true);
            this.$transactionHash.set(txHash);
            this.$transactionLink.set(this.generateTransactionLink(txHash, walletClient.chain.id));

            await this.registerTransferTransaction(txHash, formValue, walletClient.chain.id);
        } catch (e) {
            this.$transferSuccess.set(false);
            this.$transactionHash.set(null);
            this.$transactionLink.set(null);

            throw await DexKnownError.fromViem('Transfer', e);
        }

        return txHash;
    }

    private getWalletFrom(): DynWallet {
        const address = this.form.controls.from.value;

        return this.dynamicZyxDaemonService.dynRemote.userWallets$.value.find(w => w.address === address);
    }

    private generateTransactionLink(txHash: string, chainId: number): string | null {
        // Implement transaction link generation based on chain ID
        // This is a placeholder - you'll need to implement the actual logic
        // based on your blockchain explorer URLs
        const explorerUrl = this.getExplorerUrlForChain(chainId);
        return explorerUrl ? `${explorerUrl}/tx/${txHash}` : null;
    }

    private getExplorerUrlForChain(chainId: number): string | null {
        return undefined;
        // Try to get explorer URL from app config
        // try {
        //     const networkConfig = this.appConfigService.appConfig.dex.networks.find(n => n.chainId === chainId);
        //     if (networkConfig && networkConfig.blockExplorerUrls && networkConfig.blockExplorerUrls.length > 0) {
        //         return networkConfig.blockExplorerUrls[0];
        //     }
        // } catch (error) {
        //     console.warn('Could not get explorer URL from config:', error);
        // }
        //
        // // Fallback to common explorers based on chain ID
        // switch (chainId) {
        //     case 1:     // Ethereum Mainnet
        //         return 'https://etherscan.io';
        //     case 5:     // Goerli
        //         return 'https://goerli.etherscan.io';
        //     case 137:   // Polygon Mainnet
        //         return 'https://polygonscan.com';
        //     case 80001: // Mumbai
        //         return 'https://mumbai.polygonscan.com';
        //     default:
        //         return null;
        // }
    }

    private createBalanceStream(): Observable<DeliverableBalance> {
        return defer(() => {
            const walletAddress$ = getFormControlValueWithUpdatesStream(this.form.controls.from);
            const asset$ = getFormControlValueWithUpdatesStream(this.form.controls.asset);

            return walletAddress$.pipe(
                combineLatestWith(asset$),
                switchMap(([walletAddress, d]) =>
                    this.erc20BalancesSharedService.getDelBalanceStream(walletAddress, d)
                )
            );
        });
    }

    private async registerTransferTransaction(
        txHash: string,
        formValue: FormValue,
        chainId: number
    ): Promise<void> {
        try {
            const thRec = this.txHistoryRecFactory.createForNewTransfer({
                hash:        txHash,
                from:        formValue.from,
                to:          formValue.to,
                chainId:     chainId,
                deliverable: formValue.asset,
                amount:      formValue.qty
            });

            await this.txApiStorageMiddlewareService.asyncCreateTransaction(thRec);
        } catch (error) {
            console.error(`[TransferWidget] Failed to register transfer transaction: ${txHash}`, error);
            // Don't throw to avoid breaking the transfer flow
        }
    }

}
